<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';

import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from './useBrain';

// D17 binding condition: the per-inbox legacy|runtime cutover toggle is a
// real operator control (per-inbox selects), not a raw JSON textarea. It
// PATCHes hook.settings.inbox_routing on the ai_agents integration hook —
// one toggle, one revert, audited via the hook update path.
const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const { brain } = useBrain();

const inboxes = useMapGetter('inboxes/getInboxes');
const getIntegration = useMapGetter('integrations/getIntegration');

const aiAgentsHook = computed(
  () => (getIntegration.value('ai_agents')?.hooks || [])[0]
);

const inboxRouting = computed(
  () => aiAgentsHook.value?.settings?.inbox_routing || {}
);

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

onMounted(() => {
  store.dispatch('integrations/get');
  store.dispatch('inboxes/get');
});

const routingFor = inbox => inboxRouting.value[String(inbox.id)] || 'legacy';

const updateRouting = async (inbox, event) => {
  const hook = aiAgentsHook.value;
  if (!hook) return;
  try {
    await store.dispatch('integrations/updateHook', {
      hookId: hook.id,
      settings: {
        ...(hook.settings || {}),
        inbox_routing: {
          ...inboxRouting.value,
          [String(inbox.id)]: event.target.value,
        },
      },
    });
    useAlert(t('AI_AGENTS.ROUTING.SAVED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.ROUTING.ERROR'));
  }
};
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-4 max-w-3xl">
      <div>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('AI_AGENTS.ROUTING.HEADER') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.ROUTING.DESCRIPTION') }}
        </p>
      </div>

      <p v-if="!aiAgentsHook" class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.ROUTING.NO_HOOK') }}
      </p>
      <p v-else-if="!sortedInboxes.length" class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.ROUTING.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-2 m-0 list-none">
        <li
          v-for="inbox in sortedInboxes"
          :key="inbox.id"
          class="flex items-center justify-between gap-3 px-3 py-2 rounded-lg outline outline-1 outline-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ inbox.name }}
          </span>
          <select
            :value="routingFor(inbox)"
            :disabled="!isAdmin"
            class="px-2 py-1 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            @change="updateRouting(inbox, $event)"
          >
            <option value="legacy">{{ t('AI_AGENTS.ROUTING.LEGACY') }}</option>
            <option value="runtime">
              {{ t('AI_AGENTS.ROUTING.RUNTIME') }}
            </option>
          </select>
        </li>
      </ul>
    </div>
  </AgentLayout>
  <div
    v-else
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
